import { ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import type { ApiReconstructedDocumentDetail } from '@avisoma/shared';
import { Button } from '../../catalyst/button';
import { DebugObject } from '../DebugObject';
import { entityViewTitle } from '../ID';
import BackButton from '../BackButton';
import AuthGuard from '../AuthGuard';
import { ExploreInGraph } from './ExploreInGraph';

export function Header({
  entity,
  embedded = false,
}: {
  entity: ApiReconstructedDocumentDetail;
  embedded?: boolean;
}) {
  const parentDocumentId = entity.sourceDocuments[0]?.documentId;
  const downloadHref = parentDocumentId
    ? `/api/documents/${parentDocumentId}/download?${new URLSearchParams({
        reconstructedDocumentId: entity.id,
      })}`
    : null;
  const previewHref = downloadHref ? `${downloadHref}&inline=1` : null;
  const heading = entityViewTitle(entity.id, entity.suggestedFilename);

  return (
    <div className="bg-white dark:bg-zinc-800 dark:border dark:border-zinc-700 shadow sm:rounded-lg p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {!embedded && <BackButton className="shrink-0" />}
        <div className="min-w-0 grow basis-48">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-500">Reconstructed document</div>
          <h1 className="mt-1 text-lg font-semibold text-gray-900 dark:text-zinc-100 truncate" title={heading}>
            {heading}
          </h1>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="px-2 py-0.5 rounded whitespace-nowrap bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 text-xs">
              {entity.classification}
              <span className="ml-1 opacity-80">({Math.round(entity.classificationCertainty * 100)}%)</span>
            </span>
            {entity.sourceHints.title && (
              <span
                className="px-2 py-0.5 rounded truncate min-w-0 max-w-full bg-zinc-200 dark:bg-zinc-700 text-xs"
                title={entity.sourceHints.title}
              >
                {entity.sourceHints.title}
              </span>
            )}
            {entity.sourceHints.esn && (
              <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">ESN {entity.sourceHints.esn}</span>
            )}
            {entity.sourceHints.tsn && (
              <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">TSN {entity.sourceHints.tsn}</span>
            )}
            {entity.sourceHints.csn && (
              <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">CSN {entity.sourceHints.csn}</span>
            )}
            {entity.sourceHints.partNumber && (
              <span className="px-2 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900 text-xs">PN {entity.sourceHints.partNumber}</span>
            )}
          </div>

          <div className="mt-2 text-xs font-mono text-gray-500 dark:text-zinc-500 break-all">{entity.id}</div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 whitespace-nowrap">
          <DebugObject object={entity} />
          {/* The graph route is closed to the roles outside this list, as the Projects nav item is. */}
          <AuthGuard roles={['admin', 'user', 'curator']} fallback={null}>
            <ExploreInGraph entity={entity} />
          </AuthGuard>
          {previewHref && (
            <Button outline href={previewHref} target="_blank" rel="noopener noreferrer">
              <EyeIcon className="size-4" />
              Preview
            </Button>
          )}
          {downloadHref && (
            <Button outline href={downloadHref} download={entity.suggestedFilename}>
              <ArrowDownTrayIcon className="size-4" />
              Download
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
